import { useState, useRef, useEffect, useCallback } from "react"
import { Undo2, Trash2, ArrowRight, ArrowLeft, Pencil } from "lucide-react"
import { useWizard } from "./WizardContext"
import { Button } from "@/components/ui/button"

interface PixelPoint {
  x: number
  y: number
}

export default function RoofDrawingStep() {
  const { state, setRoofData, nextStep, prevStep } = useWizard()
  const [polygon, setPolygon] = useState<PixelPoint[]>([])
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Load satellite image
  useEffect(() => {
    if (!state.roofImageUrl || !canvasRef.current) return
    const canvas = canvasRef.current
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      imageRef.current = img
      canvas.width = img.width
      canvas.height = img.height
      redraw([])
    }
    img.src = state.roofImageUrl
  }, [state.roofImageUrl])

  const redraw = useCallback((points: PixelPoint[]) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx || !imageRef.current) return

    ctx.drawImage(imageRef.current, 0, 0)

    if (points.length === 0) return

    // Fill
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    ctx.closePath()
    ctx.fillStyle = "rgba(157, 27, 34, 0.18)"
    ctx.fill()

    // Stroke
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    ctx.closePath()
    ctx.strokeStyle = "#9D1B22"
    ctx.lineWidth = 3
    ctx.setLineDash([])
    ctx.stroke()

    // Vertices
    points.forEach((point, i) => {
      ctx.beginPath()
      ctx.arc(point.x, point.y, 7, 0, Math.PI * 2)
      ctx.fillStyle = i === hoveredPoint ? "#D31C33" : "#9D1B22"
      ctx.fill()
      ctx.strokeStyle = "#fff"
      ctx.lineWidth = 2.5
      ctx.stroke()
    })
  }, [hoveredPoint])

  useEffect(() => {
    redraw(polygon)
  }, [polygon, redraw])

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY
    setPolygon((prev) => [...prev, { x, y }])
  }

  const handleUndo = () => setPolygon((prev) => prev.slice(0, -1))
  const handleClear = () => setPolygon([])

  const handleConfirm = () => {
    if (polygon.length < 3 || !state.lat || !state.lng) return

    // Convert pixel polygon to lat/lng and compute area
    const imageWidth = 640
    const imageHeight = 640
    const zoom = 20
    const metersPerPixel =
      (156543.03392 * Math.cos((state.lat * Math.PI) / 180)) / Math.pow(2, zoom)

    const polygonLatLng = polygon.map((pt) => ({
      lat: state.lat! + ((imageHeight / 2 - pt.y) * metersPerPixel) / 111139,
      lng: state.lng! + ((pt.x - imageWidth / 2) * metersPerPixel) / (111139 * Math.cos((state.lat! * Math.PI) / 180)),
    }))

    // Shoelace formula in sqft
    let area = 0
    const n = polygonLatLng.length
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += polygonLatLng[i].lng * polygonLatLng[j].lat
      area -= polygonLatLng[j].lng * polygonLatLng[i].lat
    }
    const latAvg = polygonLatLng.reduce((sum, p) => sum + p.lat, 0) / n
    const feetPerDegreeLat = 364000
    const feetPerDegreeLng = 364000 * Math.cos((latAvg * Math.PI) / 180)
    const areaSqft = Math.abs(area / 2) * feetPerDegreeLat * feetPerDegreeLng

    setRoofData(polygonLatLng, Math.round(areaSqft))
    nextStep()
  }

  const calculatedArea = polygon.length >= 3 ? estimatePixelArea(polygon) : null

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Trace your roof outline
        </h2>
        <p className="text-muted-foreground mt-1">
          Click to place points around your roof edges. We'll calculate the area.
        </p>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="relative rounded-xl overflow-hidden border-2 border-muted shadow-sm bg-muted">
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          className="w-full cursor-crosshair block"
          style={{ maxHeight: "420px", objectFit: "contain" }}
        />

        {/* Overlay instructions */}
        {polygon.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
            <div className="bg-white/95 backdrop-blur rounded-lg px-5 py-3 flex items-center gap-3 shadow-lg">
              <Pencil className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-foreground">Click to start tracing your roof</span>
            </div>
          </div>
        )}

        {/* Toolbar */}
        {polygon.length > 0 && (
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              onClick={handleUndo}
              className="p-2.5 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-white transition-colors"
              title="Undo last point"
            >
              <Undo2 className="w-4 h-4 text-foreground" />
            </button>
            <button
              onClick={handleClear}
              className="p-2.5 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-white transition-colors"
              title="Clear all"
            >
              <Trash2 className="w-4 h-4 text-foreground" />
            </button>
          </div>
        )}

        {/* Area display */}
        {calculatedArea && (
          <div className="absolute bottom-3 left-3 bg-primary text-white px-4 py-2 rounded-lg shadow-lg">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Est. Area</span>
            <div className="text-lg font-bold">{calculatedArea.toLocaleString()} sq ft</div>
          </div>
        )}
      </div>

      {/* Point counter */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">{polygon.length}</span> points placed
          {polygon.length < 3 && " (minimum 3)"}
        </span>
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={prevStep}
          className="h-12 px-6 rounded-xl font-semibold"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={polygon.length < 3}
          className="flex-1 h-12 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold transition-all hover:shadow-lg hover:shadow-primary/25 disabled:opacity-40"
        >
          Confirm Roof Area
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  )
}

// Quick pixel-area estimate for display (rough, not used for final calc)
function estimatePixelArea(points: PixelPoint[]): number {
  if (points.length < 3) return 0
  let area = 0
  const n = points.length
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    area += points[i].x * points[j].y
    area -= points[j].x * points[i].y
  }
  // Rough conversion: at zoom 20, ~0.15m/px, so 1px ≈ 0.5ft, 1px² ≈ 0.25sqft
  // More precisely: at zoom 20, each pixel ≈ 0.149m ≈ 0.489ft
  const sqftPerPixelSq = 0.489 * 0.489
  return Math.round(Math.abs(area / 2) * sqftPerPixelSq)
}
